import { useEffect, useRef } from "react";

export type UseDebouncedCommitOptions<T> = {
	/** Valor em edição, por exemplo o texto do campo de busca. */
	value: T;
	/** Valor aplicado vigente, já normalizado. */
	applied: T;
	/** Atraso em milissegundos. */
	delay: number;
	/** Recebe o valor normalizado quando ele difere do aplicado. */
	onCommit: (value: T) => void;
	/** Normalização do domínio (por exemplo, `trim`). Padrão: identidade. */
	normalize?: (value: T) => T;
};

const identity = <T>(value: T) => value;

/**
 * Aplica um valor em edição depois de uma pausa, sem guardar critérios antigos.
 *
 * - Cada mudança de `value` cancela a aplicação pendente anterior.
 * - Na hora de aplicar, compara com o valor aplicado vigente: sem mudança
 *   efetiva, não chama `onCommit` (evita reiniciar a página à toa, CT05).
 * - `onCommit`, `normalize` e `applied` são lidos na hora da aplicação, então
 *   filtros e callbacks atuais são usados (CT06).
 * - Desmontar cancela a aplicação pendente.
 *
 * O valor em edição nunca é alterado pelo hook: o texto digitado, inclusive um
 * espaço no fim, fica como o usuário escreveu.
 */
export function useDebouncedCommit<T>({
	value,
	applied,
	delay,
	onCommit,
	normalize = identity,
}: UseDebouncedCommitOptions<T>) {
	const latestRef = useRef({ applied, onCommit, normalize });
	useEffect(() => {
		latestRef.current = { applied, onCommit, normalize };
	}, [applied, onCommit, normalize]);

	useEffect(() => {
		const handler = setTimeout(() => {
			const current = latestRef.current;
			const next = current.normalize(value);
			if (Object.is(next, current.normalize(current.applied))) {
				return;
			}
			current.onCommit(next);
		}, delay);

		return () => clearTimeout(handler);
	}, [value, delay]);
}
